import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService, EventsService, CustomerService, ResourceService, formatCustomerDisplayName } from "../../services";
import DatePicker from "react-datepicker";
import { Spinner, Breadcrumb, BreadcrumbItem, Button, Modal, DropdownButton, Dropdown, ProgressBar } from "react-bootstrap";
import { Columns, Download, Filter, PencilSquare, PersonSquare, Plus } from "react-bootstrap-icons";
import ReactPaginate from "react-paginate";
import { ManageTable, Export, MultiSelectDropdown } from "../../shared/components";
import { CUSTOMER_TYPE_TEXT, PROGRAM_TYPE_TEXT, PAY_SOURCE_TEXT, isCustomerSelectableForMedicalAppointment, isCustomerDischarged, formatMedicalEventCustomerNameWithDischargeStatus } from "../../shared/constants/customer.constant";
import { readPageUiObject, writePageUiObject } from "../../shared/pageFilterPersistence";
import { RESOURCE_TYPE_TEXT, formatMedicalProviderAddressDisplay, formatProviderContactDisplay, resolveEventProviderSpecialtyDisplay } from "../../shared/constants/resource.constant";
import { navigateToMedicalCreateFromRequest } from "../../shared/utils/medicalCreateEventPayload";
import { usePageScrollPinnedTableHeader } from "../../shared/hooks";

const MEDICAL_LIST_MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Max selectable span between From and To (inclusive window cap). */
const MEDICAL_LIST_MAX_RANGE_DAYS = 366;
/** Default From→To span on first load (user can widen up to MAX_RANGE_DAYS). */
const MEDICAL_LIST_DEFAULT_RANGE_DAYS = 90;
/** Debounce before loading events after a From/To edit. */
const MEDICAL_LIST_DATE_EDIT_DEBOUNCE_MS = 3000;
/** Quick debounce for non-date triggers (initial load once lookups resolve). */
const MEDICAL_LIST_INITIAL_DEBOUNCE_MS = 200;
/** Rows decorated per idle slice before yielding back to the browser. */
const MEDICAL_LIST_DECORATE_CHUNK_SIZE = 250;
/** Client-side page size for the multi-day list table. */
const MEDICAL_LIST_PAGE_SIZE = 100;

const getMedicalListDefaultToDate = (from = new Date()) =>
  new Date(from.getTime() + MEDICAL_LIST_DEFAULT_RANGE_DAYS * MEDICAL_LIST_MS_PER_DAY);

/** Lean API rows expose `_id`; normalize to `id` for existing list logic. */
function normalizeMedicalListCustomers(raw = []) {
  return raw.map((c) => ({
    ...c,
    id: c?.id ?? (c?._id != null ? String(c._id) : undefined),
  }));
}

const medicalCustomerIdMatches = (a, b) => {
  if (a == null || b == null) return false;
  try {
    const sa = String(a).trim();
    const sb = String(b).trim();
    if (!sa || !sb) return false;
    if (sa === sb) return true;
    if (sa.length === 24 && sb.length === 24 && /^[a-f0-9]+$/i.test(sa) && /^[a-f0-9]+$/i.test(sb)) {
      return sa.toLowerCase() === sb.toLowerCase();
    }
    return false;
  } catch (_e) {
    return false;
  }
};

/** Trailing decorative asterisks (ASCII or fullwidth) on event `client_name` — strip for matching. */
const stripTrailingMedicalClientNameDecorations = (s) =>
  String(s || "")
    .replace(/[\s\u00A0\u200B-\u200D\uFEFF]*[\*＊﹡⋆⁎]+[\s\u00A0\u200B-\u200D\uFEFF]*$/gu, "")
    .trim();

const normMedicalCustomerLabel = (s) =>
  String(stripTrailingMedicalClientNameDecorations(s) || "")
    .toLowerCase()
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Human-readable resource type: full resource, else event `data.resource_type` snapshot. */
function getMedicalEventProviderTypeDisplay(item, currentResource) {
  if (currentResource) {
    const t = currentResource.type;
    if (t === 'other' && currentResource.type_other) {
      const base = RESOURCE_TYPE_TEXT[t] || t;
      return base ? `${base} (${currentResource.type_other})` : currentResource.type_other;
    }
    if (t) return RESOURCE_TYPE_TEXT[t] || t;
    return '';
  }
  const raw = item?.data?.resource_type;
  if (!raw) return '';
  if (raw === 'other' && item?.data?.resource_type_other) {
    const base = RESOURCE_TYPE_TEXT[raw] || raw;
    return base ? `${base} (${item.data.resource_type_other})` : item.data.resource_type_other;
  }
  return RESOURCE_TYPE_TEXT[raw] || raw || '';
}

function buildMedicalCustomerNameVariants(c) {
  const variants = new Set();
  const add = (v) => {
    const n = normMedicalCustomerLabel(v);
    if (n) variants.add(n);
  };
  if (c?.name) add(c.name);
  const fn = String(c?.firstname || "").trim();
  const ln = String(c?.lastname || "").trim();
  if (fn && ln) {
    add(`${ln}, ${fn}`);
    add(`${fn} ${ln}`);
    add(`${ln} ${fn}`);
  }
  return variants;
}

/** Normalize an id-like value (handles Mongo ObjectId) to a trimmed string, or null. */
function normalizeMedicalIdValue(v) {
  if (v == null || v === "") return null;
  let x = v;
  if (typeof x?.toHexString === "function") x = x.toHexString();
  const s = String(x).trim();
  return s || null;
}

/**
 * Precompute O(1) lookup maps for customers so resolving an event's customer is constant-time
 * instead of scanning the whole `customers` array (per event) — the main cause of the page freeze.
 */
function buildMedicalCustomerIndex(customers = []) {
  const byId = new Map();
  const byNormName = new Map();
  const byVariant = new Map();
  const normNameList = [];
  const addIdKey = (id, c) => {
    if (!id) return;
    if (!byId.has(id)) byId.set(id, c);
    if (id.length === 24 && /^[a-f0-9]+$/i.test(id)) {
      const lower = id.toLowerCase();
      if (!byId.has(lower)) byId.set(lower, c);
    }
  };
  for (const c of customers) {
    addIdKey(normalizeMedicalIdValue(c?.id), c);
    addIdKey(normalizeMedicalIdValue(c?._id), c);
    const mn = normMedicalCustomerLabel(c?.name);
    if (mn) {
      if (!byNormName.has(mn)) byNormName.set(mn, c);
      normNameList.push({ c, mn });
    }
    for (const v of buildMedicalCustomerNameVariants(c)) {
      if (!byVariant.has(v)) byVariant.set(v, c);
    }
  }
  return { byId, byNormName, byVariant, normNameList };
}

/**
 * Resolve Customer for a medical event when id compare fails (string/ObjectId) or ref is stale.
 * Matches `client_name` / `target_name` to `name` or firstname+lastname forms (e.g. "You, Yu Y").
 * Pass `index` (from buildMedicalCustomerIndex) for O(1) lookups; falls back to a linear scan otherwise.
 */
function resolveMedicalEventCustomer(event, customers = [], index = null) {
  if (!customers.length) return undefined;

  const idCandidates = [];
  const pushId = (v) => {
    const s = normalizeMedicalIdValue(v);
    if (s && !idCandidates.includes(s)) idCandidates.push(s);
  };
  pushId(event?.data?.customer);
  pushId(event?.target_uuid);

  if (index) {
    for (const id of idCandidates) {
      const hit = index.byId.get(id) || (id.length === 24 ? index.byId.get(id.toLowerCase()) : undefined);
      if (hit) return hit;
    }
    const label = String(event?.data?.client_name || event?.target_name || "").trim();
    if (!label) return undefined;
    const nLabel = normMedicalCustomerLabel(label);
    const exact = index.byNormName.get(nLabel) || index.byVariant.get(nLabel);
    if (exact) return exact;
    for (const { c, mn } of index.normNameList) {
      if (mn === nLabel || mn.includes(nLabel) || nLabel.includes(mn)) return c;
    }
    return undefined;
  }

  for (const id of idCandidates) {
    const hit = customers.find(
      (m) =>
        medicalCustomerIdMatches(m?.id, id) ||
        medicalCustomerIdMatches(m?._id, id)
    );
    if (hit) return hit;
  }

  const label = String(event?.data?.client_name || event?.target_name || "").trim();
  if (!label) return undefined;
  const nLabel = normMedicalCustomerLabel(label);
  let c = customers.find((m) => normMedicalCustomerLabel(m?.name) === nLabel);
  if (c) return c;
  c = customers.find((m) => buildMedicalCustomerNameVariants(m).has(nLabel));
  if (c) return c;
  c = customers.find((m) => {
    const mn = normMedicalCustomerLabel(m?.name);
    return mn && (mn === nLabel || mn.includes(nLabel) || nLabel.includes(mn));
  });
  return c || undefined;
}

function buildCustomerNameForMedicalListRow(item, currentCustomer) {
  if (currentCustomer) {
    return formatMedicalEventCustomerNameWithDischargeStatus(
      formatCustomerDisplayName(currentCustomer),
      currentCustomer
    );
  }
  const customerName = item?.data?.client_name || item?.target_name || "";
  return `${customerName || ""}`.trim();
}

function medicalListDisplayText(value) {
  if (value === undefined || value === null) return '';
  const normalized = `${value}`.trim();
  if (!normalized) return '';
  const lowered = normalized.toLowerCase();
  if (lowered === 'yes') return 'Yes';
  if (lowered === 'no') return 'No';
  return normalized;
}

function medicalListOptionLabel(options = [], value = '') {
  const normalizedValue = `${value || ''}`.trim().toLowerCase();
  const option = options.find((item) => `${item?.value || ''}`.trim().toLowerCase() === normalizedValue);
  return option?.label || `${value || ''}`;
}

function medicalListCustomerPaySource(customer) {
  if (!customer) return '';
  const paySource = customer?.pay_source || '';
  const paySourceLabel = PAY_SOURCE_TEXT[paySource] || paySource;
  if (paySource === 'other' && customer?.pay_source_other) {
    return `${paySourceLabel} (${customer.pay_source_other})`;
  }
  return paySourceLabel;
}

function medicalListCustomerEyesOn(currentCustomer, event) {
  if (currentCustomer) {
    const fromEyesOn = medicalListDisplayText(currentCustomer.eyes_on || '');
    if (fromEyesOn === 'Yes' || fromEyesOn === 'No') return fromEyesOn;
    return currentCustomer.disability ? 'Yes' : 'No';
  }
  return event?.data?.disability?.toLowerCase() === 'yes' ? 'Yes' : 'No';
}

function medicalListAppointmentTime(item) {
  if (!item?.start_time) return '';
  const d = new Date(item.start_time);
  return `${EventsService.formatDate(d)} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: 'numeric', hour12: true })}`;
}

/**
 * Decorate a single medical event with all display fields used by the list/export.
 * Resolves the customer/resource exactly once (via the precomputed indexes) — shared by the
 * initial fetch and `confirmEvent` so both stay fast and consistent.
 */
function getMedicalEventId(event) {
  const id = event?.id ?? event?._id;
  return id != null ? String(id) : "";
}

function decorateMedicalEventRow(item, ctx) {
  if (item?.id == null && item?._id != null) {
    item.id = String(item._id);
  }
  const { customers, customerIndex, resourceById } = ctx;
  const currentCustomer = resolveMedicalEventCustomer(item, customers, customerIndex);
  const resourceId = item?.data?.resource;
  const currentResource = resourceId != null ? resourceById.get(String(resourceId)) : undefined;

  item.customer_name = buildCustomerNameForMedicalListRow(item, currentCustomer);
  item.customer_type = CUSTOMER_TYPE_TEXT[currentCustomer?.type] || currentCustomer?.type || '';
  item.program_type = PROGRAM_TYPE_TEXT[currentCustomer?.program_type] || currentCustomer?.program_type || '';
  item.pay_source = medicalListCustomerPaySource(currentCustomer);
  item.dob = item?.data?.customer ? (currentCustomer?.birth_date || '') : (item?.data?.client_birth_date || '');
  item.language_support = EventsService.getMedicalLanguageSupportDisplay(item?.data?.interpreter || '');
  item.transportation_support = EventsService.getMedicalTransportationSupportDisplay(item?.data?.trans_method);
  item.driver = item?.link_event_name || '';
  item.appointment_time = medicalListAppointmentTime(item);
  item.eyes_on = medicalListCustomerEyesOn(currentCustomer, item);
  item.has_airtag = currentCustomer ? medicalListDisplayText(currentCustomer.has_airtag || '') : '';
  item.lift_equipped_vehicle = currentCustomer ? medicalListDisplayText(currentCustomer.wheelchair || '') : '';
  item.new_patient = medicalListDisplayText(item?.data?.new_patient || '');
  item.doctor_order = medicalListDisplayText(item?.data?.doc_order || '');
  item.fasting_required = item?.data?.fasting || '';
  item.id_needed = medicalListDisplayText(item?.data?.need_id || '');
  item.need_medication_list = medicalListDisplayText(item?.data?.need_med_list || '');
  item.provider = resourceId ? (currentResource?.name || item?.data?.resource_name || '') : (item?.data?.resource_name || '');
  item.specialty = resolveEventProviderSpecialtyDisplay(currentResource, item?.data);
  item.provider_type = getMedicalEventProviderTypeDisplay(item, currentResource);
  item.provider_phone_number = formatProviderContactDisplay(currentResource, item?.data);
  item.provider_address = formatMedicalProviderAddressDisplay(currentResource, item?.data);
  item.notes = item?.data?.notes || '';
  item.reason = item?.data?.reason || '';
  item.requirement = item?.data?.other || '';
  item.confirmed = item?.confirmed || false;
  return item;
}

/** Align with API `disableEvent` (`inactive`). Missing/legacy status treated as active. */
function medicalListEventStatusBucket(event) {
  const s = `${event?.status ?? ""}`.trim().toLowerCase();
  if (s === "inactive" || s === "archived") return "inactive";
  return "active";
}

const EventsMultipleFilterMenu = React.forwardRef(function EventsMultipleFilterMenu(
  {
    style,
    className,
    labeledBy,
    draftCustomerTypeFilter,
    setDraftCustomerTypeFilter,
    customerTypeOptions,
    draftProgramTypeFilter,
    setDraftProgramTypeFilter,
    programTypeOptions,
    draftPaySourceFilter,
    setDraftPaySourceFilter,
    paySourceOptions,
    draftLanguageSupportFilter,
    setDraftLanguageSupportFilter,
    languageSupportOptions,
    draftTransportationSupportFilter,
    setDraftTransportationSupportFilter,
    transportationSupportOptions,
    draftEyesOnFilter,
    setDraftEyesOnFilter,
    eyesOnOptions,
    draftNewPatientFilter,
    setDraftNewPatientFilter,
    newPatientOptions,
    draftDoctorOrderFilter,
    setDraftDoctorOrderFilter,
    doctorOrderOptions,
    draftFastingRequiredFilter,
    setDraftFastingRequiredFilter,
    fastingRequiredOptions,
    draftIdNeededFilter,
    setDraftIdNeededFilter,
    idNeededOptions,
    draftNeedMedicationListFilter,
    setDraftNeedMedicationListFilter,
    needMedicationListOptions,
    cleanFilterAndClose,
    FilterAndClose,
  },
  ref
) {
  return (
    <div
      ref={ref}
      style={style}
      className={[className, 'events-multiple-filter-menu'].filter(Boolean).join(' ')}
      aria-labelledby={labeledBy}
    >
      <h6>Filter By</h6>
      <div className="events-multiple-filter-menu__grid">
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Customer Type</div>
          <select value={draftCustomerTypeFilter} onChange={(e) => setDraftCustomerTypeFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {customerTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Program Type</div>
          <select value={draftProgramTypeFilter} onChange={(e) => setDraftProgramTypeFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {programTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Pay Source</div>
          <select value={draftPaySourceFilter} onChange={(e) => setDraftPaySourceFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {paySourceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Language Support</div>
          <select value={draftLanguageSupportFilter} onChange={(e) => setDraftLanguageSupportFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {languageSupportOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Transportation Support</div>
          <select value={draftTransportationSupportFilter} onChange={(e) => setDraftTransportationSupportFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {transportationSupportOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Eyes-On</div>
          <select value={draftEyesOnFilter} onChange={(e) => setDraftEyesOnFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {eyesOnOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">New Patient</div>
          <select value={draftNewPatientFilter} onChange={(e) => setDraftNewPatientFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {newPatientOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Doctor's Order</div>
          <select value={draftDoctorOrderFilter} onChange={(e) => setDraftDoctorOrderFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {doctorOrderOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Fasting Required</div>
          <select value={draftFastingRequiredFilter} onChange={(e) => setDraftFastingRequiredFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {fastingRequiredOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">ID Needed</div>
          <select value={draftIdNeededFilter} onChange={(e) => setDraftIdNeededFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {idNeededOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="events-multiple-filter-menu__field">
          <div className="field-label">Need Medication List</div>
          <select value={draftNeedMedicationListFilter} onChange={(e) => setDraftNeedMedicationListFilter(e.currentTarget.value)}>
            <option value="">All</option>
            {needMedicationListOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
      </div>
      <div className="events-multiple-filter-menu__footer">
        <button type="button" className="btn btn-default btn-sm" onClick={() => cleanFilterAndClose()}> Cancel </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => FilterAndClose()}> Filter </button>
      </div>
    </div>
  );
});

const EventsMultipleList = () => {
  const navigate = useNavigate();
  // Guards against rapid date switching: ignore stale responses and cancel superseded requests
  // so concurrent in-flight fetches don't pile up large payloads in memory and freeze the tab.
  const eventsFetchRequestIdRef = useRef(0);
  const eventsAbortRef = useRef(null);
  // True when the next fetch was triggered by a From/To edit, so it waits the longer debounce.
  const dateEditDebounceRef = useRef(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(12);
  const [events, setEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sorting, setSorting] = useState({key: 'appointment_time', order: 'asc'});
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemOffset, setItemOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [resources, setResources] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(() => getMedicalListDefaultToDate(new Date()));
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [programTypeFilter, setProgramTypeFilter] = useState('');
  const [paySourceFilter, setPaySourceFilter] = useState('');
  const [languageSupportFilter, setLanguageSupportFilter] = useState('');
  const [transportationSupportFilter, setTransportationSupportFilter] = useState('');
  const [eyesOnFilter, setEyesOnFilter] = useState('');
  const [newPatientFilter, setNewPatientFilter] = useState('');
  const [doctorOrderFilter, setDoctorOrderFilter] = useState('');
  const [fastingRequiredFilter, setFastingRequiredFilter] = useState('');
  const [idNeededFilter, setIdNeededFilter] = useState('');
  const [needMedicationListFilter, setNeedMedicationListFilter] = useState('');
  /** When true, list/export/select-all use inactive appointments only; otherwise active only. */
  const [showInactiveAppointmentsOnly, setShowInactiveAppointmentsOnly] = useState(false);
  // Draft values shown inside dropdown; only applied when user taps "Filter".
  const [draftCustomerTypeFilter, setDraftCustomerTypeFilter] = useState('');
  const [draftProgramTypeFilter, setDraftProgramTypeFilter] = useState('');
  const [draftPaySourceFilter, setDraftPaySourceFilter] = useState('');
  const [draftLanguageSupportFilter, setDraftLanguageSupportFilter] = useState('');
  const [draftTransportationSupportFilter, setDraftTransportationSupportFilter] = useState('');
  const [draftEyesOnFilter, setDraftEyesOnFilter] = useState('');
  const [draftNewPatientFilter, setDraftNewPatientFilter] = useState('');
  const [draftDoctorOrderFilter, setDraftDoctorOrderFilter] = useState('');
  const [draftFastingRequiredFilter, setDraftFastingRequiredFilter] = useState('');
  const [draftIdNeededFilter, setDraftIdNeededFilter] = useState('');
  const [draftNeedMedicationListFilter, setDraftNeedMedicationListFilter] = useState('');
  /** Multi-select filters (same UI as customer update): { value, label }[] */
  const [selectedCustomerFilters, setSelectedCustomerFilters] = useState([]);
  const [selectedResourceFilters, setSelectedResourceFilters] = useState([]);
  const [columns, setColumns] = useState([
    {
      key: 'customer_name',
      label:'Customer Name',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'customer_type',
      label: 'Customer Type',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'program_type',
      label: 'Program Type',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'pay_source',
      label: 'Pay Source',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'dob',
      label: 'Date Of Birth',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'language_support',
      label: 'Language Support',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'transportation_support',
      label: 'Transportation Support',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'driver',
      label: 'Driver',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'appointment_time',
      label: 'Appointment Time',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'eyes_on',
      label: 'Eyes-On',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'has_airtag',
      label: 'Has Airtag',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'lift_equipped_vehicle',
      label: 'Lift-Equipped Vehicle Required',
      show: true,
      section: 'Customer Information',
    },
    {
      key: 'new_patient',
      label: 'New Patient',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'doctor_order',
      label: "Doctor's Order",
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'fasting_required',
      label: 'Fasting Required',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'id_needed',
      label: 'ID Needed',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'need_medication_list',
      label: 'Need Medication List',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'provider',
      label: 'Provider',
      show: true,
      section: 'Provider Information',
    },
    {
      key: 'specialty',
      label: 'Specialty',
      show: true,
      section: 'Provider Information',
    },
    {
      key: 'provider_type',
      label: 'Provider Type',
      show: true,
      section: 'Provider Information',
    },
    {
      key: 'provider_phone_number',
      label: 'Provider Contact',
      show: true,
      section: 'Provider Information',
    },
    {
      key: 'provider_address',
      label: 'Provider Address',
      show: true,
      section: 'Provider Information',
    },
    {
      key: 'notes',
      label: 'Notes',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'reason',
      label: 'Reason',
      show: true,
      section: 'Appointment Details',
    },
    {
      key: 'requirement',
      label: 'Requirement',
      show: true,
      section: 'Appointment Details',
    }
  ]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  useEffect(() => {
    const saved = readPageUiObject("events_multiple_list");
    if (saved) {
      if (saved.sorting && typeof saved.sorting === "object") setSorting(saved.sorting);
      // From/To intentionally not restored: always default to today .. today + 90 days.
      if (typeof saved.customerTypeFilter === "string") setCustomerTypeFilter(saved.customerTypeFilter);
      if (typeof saved.programTypeFilter === "string") setProgramTypeFilter(saved.programTypeFilter);
      if (typeof saved.paySourceFilter === "string") setPaySourceFilter(saved.paySourceFilter);
      if (typeof saved.languageSupportFilter === "string") setLanguageSupportFilter(saved.languageSupportFilter);
      if (typeof saved.transportationSupportFilter === "string")
        setTransportationSupportFilter(saved.transportationSupportFilter);
      if (typeof saved.eyesOnFilter === "string") setEyesOnFilter(saved.eyesOnFilter);
      if (typeof saved.newPatientFilter === "string") setNewPatientFilter(saved.newPatientFilter);
      if (typeof saved.doctorOrderFilter === "string") setDoctorOrderFilter(saved.doctorOrderFilter);
      if (typeof saved.fastingRequiredFilter === "string") setFastingRequiredFilter(saved.fastingRequiredFilter);
      if (typeof saved.idNeededFilter === "string") setIdNeededFilter(saved.idNeededFilter);
      if (typeof saved.needMedicationListFilter === "string")
        setNeedMedicationListFilter(saved.needMedicationListFilter);
      // Customer / Resource multi-selects intentionally not restored: reset on refresh.
      if (typeof saved.showInactiveAppointmentsOnly === "boolean")
        setShowInactiveAppointmentsOnly(saved.showInactiveAppointmentsOnly);
    }
    setFiltersHydrated(true);
  }, []);

  const tableFiltersSnapshot = useMemo(
    () => ({
      sorting,
      customerTypeFilter,
      programTypeFilter,
      paySourceFilter,
      languageSupportFilter,
      transportationSupportFilter,
      eyesOnFilter,
      newPatientFilter,
      doctorOrderFilter,
      fastingRequiredFilter,
      idNeededFilter,
      needMedicationListFilter,
      showInactiveAppointmentsOnly,
    }),
    [
      sorting,
      customerTypeFilter,
      programTypeFilter,
      paySourceFilter,
      languageSupportFilter,
      transportationSupportFilter,
      eyesOnFilter,
      newPatientFilter,
      doctorOrderFilter,
      fastingRequiredFilter,
      idNeededFilter,
      needMedicationListFilter,
      showInactiveAppointmentsOnly,
    ]
  );
  useEffect(() => {
    if (!filtersHydrated) return;
    const t = setTimeout(() => {
      writePageUiObject("events_multiple_list", tableFiltersSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [filtersHydrated, tableFiltersSnapshot]);

  const normalizeFilterText = (value) => String(value || '').trim().toLowerCase();

  // O(1) lookup indexes so resolving an event's customer/resource never scans the full arrays.
  const customerIndex = useMemo(() => buildMedicalCustomerIndex(customers), [customers]);
  const resourceById = useMemo(() => {
    const map = new Map();
    for (const r of resources || []) {
      if (r?.id != null) map.set(String(r.id), r);
    }
    return map;
  }, [resources]);
  const medicalRowContext = useMemo(
    () => ({ customers, customerIndex, resourceById }),
    [customers, customerIndex, resourceById]
  );

  const customerFilterOptions = useMemo(
    () =>
      [...customers]
        .filter(
          (c) =>
            c?.name &&
            (isCustomerSelectableForMedicalAppointment(c) || isCustomerDischarged(c))
        )
        .map((c) => ({
          value: String(c.id ?? c._id),
          label: formatMedicalEventCustomerNameWithDischargeStatus(
            formatCustomerDisplayName(c),
            c
          ),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [customers]
  );

  // Keep selected filter chip labels in sync with customer-info name format.
  useEffect(() => {
    if (!selectedCustomerFilters?.length || !customerFilterOptions.length) return;
    const byValue = new Map(customerFilterOptions.map((o) => [String(o.value), o.label]));
    let changed = false;
    const next = selectedCustomerFilters.map((sel) => {
      const fresh = byValue.get(String(sel.value));
      if (fresh != null && fresh !== sel.label) {
        changed = true;
        return { ...sel, label: fresh };
      }
      return sel;
    });
    if (changed) setSelectedCustomerFilters(next);
  }, [customerFilterOptions, selectedCustomerFilters]);

  const resourceFilterOptions = useMemo(
    () =>
      [...resources]
        .filter((r) => r?.name)
        .map((r) => ({ value: String(r.id), label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [resources]
  );

  const eventMatchesSelectedCustomers = (event, selectedIds) => {
    if (!selectedIds?.size) return true;
    const dataCustomerId = event?.data?.customer;
    if (dataCustomerId && selectedIds.has(String(dataCustomerId))) return true;
    const clientName = normalizeFilterText(event?.data?.client_name || event?.target_name);
    if (!clientName) return false;
    const matched =
      customerIndex.byNormName.get(clientName) || customerIndex.byVariant.get(clientName);
    if (matched) {
      const matchedId = matched.id != null ? String(matched.id) : matched._id != null ? String(matched._id) : '';
      return matchedId ? selectedIds.has(matchedId) : false;
    }
    return false;
  };

  const eventMatchesSelectedResources = (event, selectedIds) => {
    if (!selectedIds?.size) return true;
    const dataResourceId = event?.data?.resource;
    if (dataResourceId && selectedIds.has(String(dataResourceId))) return true;
    const resName = normalizeFilterText(event?.data?.resource_name);
    if (!resName) return false;
    for (const id of selectedIds) {
      const r = resourceById.get(id);
      if (r && normalizeFilterText(r.name) === resName) return true;
    }
    return false;
  };

  /** Hide discharged/inactive customer appointments unless that customer is explicitly chosen in the Customer filter. */
  const isDischargedCustomerRowHiddenUnlessSelected = (event) => {
    const cust = resolveMedicalEventCustomer(event, customers, customerIndex);
    if (!cust || !isCustomerDischarged(cust)) return false;
    const custId = cust.id != null ? String(cust.id) : cust._id != null ? String(cust._id) : '';
    if (!custId) return true;
    return !selectedCustomerFilters?.some((sel) => String(sel.value) === custId);
  };

  const sortEventsByKey = (eventsList = [], key = '', order = 'asc') => {
    if (!key) return [...eventsList];
    const sorted = [...eventsList].sort((a, b) => {
      if (key === 'appointment_time') {
        const aTime = a?.start_time ? new Date(a.start_time).getTime() : 0;
        const bTime = b?.start_time ? new Date(b.start_time).getTime() : 0;
        return aTime - bTime;
      }
      const aValue = `${a?.[key] || ''}`.toLowerCase();
      const bValue = `${b?.[key] || ''}`.toLowerCase();
      return aValue.localeCompare(bValue);
    });
    return order === 'desc' ? sorted.reverse() : sorted;
  };

  useEffect(() => {
    if (!AuthService.canViewMedicalEvents()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    let cancelled = false;
    Promise.all([
      CustomerService.getCustomersForMedicalList(),
      ResourceService.getAll(),
    ]).then(([customerRes, resourceRes]) => {
      if (cancelled) return;
      setCustomers(normalizeMedicalListCustomers(customerRes?.data || []));
      setResources(resourceRes?.data || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Latest sorting without re-triggering the fetch effect (a separate effect re-sorts on change).
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;

  useEffect(() => {
    if (!(fromDate && toDate && customers?.length > 0 && resources?.length > 0)) return undefined;
    const requestId = ++eventsFetchRequestIdRef.current;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let cancelled = false;
    const isStale = () => cancelled || requestId !== eventsFetchRequestIdRef.current;
    // From/To edits (typed or picked) wait 3s before loading; initial load stays quick.
    const isDateEdit = dateEditDebounceRef.current;
    const debounceMs = isDateEdit ? MEDICAL_LIST_DATE_EDIT_DEBOUNCE_MS : MEDICAL_LIST_INITIAL_DEBOUNCE_MS;
    dateEditDebounceRef.current = false;
    // For date edits, defer the overlay until the fetch actually fires — otherwise the modal
    // pops up mid-typing and its backdrop steals focus, preventing the user from finishing the date.
    if (!isDateEdit) {
      setShowLoadingOverlay(true);
      setLoadingProgress(12);
    }
    const timer = setTimeout(async () => {
      if (isDateEdit) {
        setShowLoadingOverlay(true);
        setLoadingProgress(12);
      }
      // Cancel any earlier still-pending request before issuing this one.
      if (eventsAbortRef.current) eventsAbortRef.current.abort();
      eventsAbortRef.current = controller;
      try {
        const data = await EventsService.getAllEvents(
          {
            from: EventsService.formatDate(fromDate),
            to: EventsService.formatDate(toDate),
            type: 'medical',
          },
          controller ? { signal: controller.signal } : {}
        );
        if (isStale()) return;
        const medicalEvents = data?.data || [];
        // Decorate rows in chunks, yielding to the browser between chunks so a large
        // result set never blocks the main thread long enough to freeze the tab.
        const CHUNK_SIZE = MEDICAL_LIST_DECORATE_CHUNK_SIZE;
        const processed = [];
        for (let i = 0; i < medicalEvents.length; i += CHUNK_SIZE) {
          if (isStale()) return;
          const end = Math.min(i + CHUNK_SIZE, medicalEvents.length);
          for (let j = i; j < end; j++) {
            processed.push(decorateMedicalEventRow(medicalEvents[j], medicalRowContext));
          }
          const fraction = medicalEvents.length ? end / medicalEvents.length : 1;
          setLoadingProgress(Math.min(96, 12 + Math.round(fraction * 84)));
          if (end < medicalEvents.length) {
            // Hand control back to the browser so it can paint / stay responsive.
            await new Promise((resolve) => { setTimeout(resolve, 0); });
          }
        }
        if (isStale()) return;
        const activeSorting = sortingRef.current;
        setEvents(sortEventsByKey(processed, activeSorting?.key || 'appointment_time', activeSorting?.order || 'asc'));
      } catch (e) {
        // Ignore aborted/superseded requests; only relevant errors belong to the latest request.
      } finally {
        if (eventsAbortRef.current === controller) eventsAbortRef.current = null;
        // Only the latest request dismisses the overlay; stale/aborted ones leave it for the newer fetch.
        if (requestId === eventsFetchRequestIdRef.current) {
          setLoadingProgress(100);
          setShowLoadingOverlay(false);
        }
      }
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [fromDate, toDate, customers, resources]);

  useEffect(() => {
    if (!showLoadingOverlay) return undefined;
    const timer = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.max(1, Math.ceil((90 - prev) / 8));
      });
    }, 220);
    return () => clearInterval(timer);
  }, [showLoadingOverlay]);

  const selectedCustomerIds = useMemo(
    () => new Set((selectedCustomerFilters || []).map((s) => String(s.value))),
    [selectedCustomerFilters]
  );
  const selectedResourceIds = useMemo(
    () => new Set((selectedResourceFilters || []).map((s) => String(s.value))),
    [selectedResourceFilters]
  );

  const filteredEvents = useMemo(() => {
    if (!events?.length) return [];
    return events.filter((event) => (
      !isDischargedCustomerRowHiddenUnlessSelected(event) &&
      eventMatchesSelectedCustomers(event, selectedCustomerIds) &&
      eventMatchesSelectedResources(event, selectedResourceIds) &&
      (!customerTypeFilter || event?.customer_type === customerTypeFilter) &&
      (!programTypeFilter || event?.program_type === programTypeFilter) &&
      (!paySourceFilter || event?.pay_source === paySourceFilter) &&
      (!languageSupportFilter || event?.language_support === languageSupportFilter) &&
      (!transportationSupportFilter || event?.transportation_support === transportationSupportFilter) &&
      (!eyesOnFilter || event?.eyes_on === eyesOnFilter) &&
      (!newPatientFilter || event?.new_patient === newPatientFilter) &&
      (!doctorOrderFilter || event?.doctor_order === doctorOrderFilter) &&
      (!fastingRequiredFilter || event?.fasting_required === fastingRequiredFilter) &&
      (!idNeededFilter || event?.id_needed === idNeededFilter) &&
      (!needMedicationListFilter || event?.need_medication_list === needMedicationListFilter)
    ));
  }, [
    events,
    customerIndex,
    selectedCustomerIds,
    selectedResourceIds,
    selectedCustomerFilters,
    customerTypeFilter,
    programTypeFilter,
    paySourceFilter,
    languageSupportFilter,
    transportationSupportFilter,
    eyesOnFilter,
    newPatientFilter,
    doctorOrderFilter,
    fastingRequiredFilter,
    idNeededFilter,
    needMedicationListFilter,
  ]);

  const listStatusFilter = showInactiveAppointmentsOnly ? "inactive" : "active";
  const visibleRowsForList = useMemo(
    () => filteredEvents.filter((ev) => medicalListEventStatusBucket(ev) === listStatusFilter),
    [filteredEvents, listStatusFilter]
  );

  const pageCount = Math.max(1, Math.ceil((visibleRowsForList.length || 0) / MEDICAL_LIST_PAGE_SIZE));
  const safeItemOffset = useMemo(() => {
    const maxOffset = Math.max(0, (pageCount - 1) * MEDICAL_LIST_PAGE_SIZE);
    return Math.min(itemOffset, maxOffset);
  }, [itemOffset, pageCount]);

  const pagedRowsForList = useMemo(
    () => visibleRowsForList.slice(safeItemOffset, safeItemOffset + MEDICAL_LIST_PAGE_SIZE),
    [visibleRowsForList, safeItemOffset]
  );

  const resetListPage = () => {
    setItemOffset(0);
    setCurrentPage(1);
  };

  useEffect(() => {
    resetListPage();
  }, [
    fromDate,
    toDate,
    listStatusFilter,
    selectedCustomerFilters,
    selectedResourceFilters,
    customerTypeFilter,
    programTypeFilter,
    paySourceFilter,
    languageSupportFilter,
    transportationSupportFilter,
    eyesOnFilter,
    newPatientFilter,
    doctorOrderFilter,
    fastingRequiredFilter,
    idNeededFilter,
    needMedicationListFilter,
    sorting.key,
    sorting.order,
  ]);

  useEffect(() => {
    const maxOffset = Math.max(0, (pageCount - 1) * MEDICAL_LIST_PAGE_SIZE);
    if (itemOffset > maxOffset) {
      setItemOffset(maxOffset);
      setCurrentPage(Math.floor(maxOffset / MEDICAL_LIST_PAGE_SIZE) + 1);
    }
  }, [pageCount, itemOffset]);

  useEffect(() => {
    setEvents((prevEvents) => sortEventsByKey(prevEvents, sorting.key, sorting.order || 'asc'));
  }, [sorting]);

  /* absolute positioning (Popper default) avoids first-open misplacement vs scroll parents; fixed often wrong on first paint */
  const filterMenuPopperConfig = useMemo(
    () => ({
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: typeof document !== 'undefined' ? document.documentElement : undefined,
            padding: { top: 12, right: 28, bottom: 12, left: 12 },
            altAxis: true,
          },
        },
        { name: 'flip', enabled: true },
      ],
    }),
    []
  );

  // Popper measures before the custom menu finishes layout on first open; trigger a follow-up update.
  useLayoutEffect(() => {
    if (!showFilterDropdown) return undefined;
    const bump = () => window.dispatchEvent(new Event('resize'));
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => bump());
    });
    const t0 = window.setTimeout(bump, 0);
    const t1 = window.setTimeout(bump, 32);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t0);
      clearTimeout(t1);
    };
  }, [showFilterDropdown]);

  const confirmEvent = (id) => {
    if (!AuthService.canEditMedicalEvents()) return;
    EventsService.updateEvent(id, { confirmed: true }).then(() => {
      // Keep row order stable after confirm; re-sort only on column click or page refresh.
      setEvents((prevEvents) =>
        prevEvents.map((item) => (getMedicalEventId(item) === id ? { ...item, confirmed: true } : item))
      );
    });
  };


  const redirectToAdmin = () => {
    navigate(`/dashboard/dashboard`)
  }

  const goToCreateNew = () => {
    if (!AuthService.canEditMedicalEvents()) return;
    const seedEvent = {
      data: {},
      type: 'medical',
      status: 'active',
      start_time: new Date(),
      stop_time: new Date(),
      edit_history: []
    };
    navigateToMedicalCreateFromRequest(navigate, seedEvent);
  }


  const openMedicalEventPageInNewTab = (path) => {
    const url = `${window.location.origin}${path}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCustomerNameClick = (medicalEvent) => {
    const eventId = getMedicalEventId(medicalEvent);
    if (!eventId) return;
    if (AuthService.canEditMedicalEvents()) {
      openMedicalEventPageInNewTab(`/medical/events/edit/${eventId}`);
      return;
    }
    openMedicalEventPageInNewTab(`/medical/events/${eventId}`);
  }

  const getSortingImg = (key) => {
    return sorting.key === key ? (sorting.order === 'asc' ? 'up_arrow' : 'down_arrow') : 'default';
  }

  const sortTableWithField = (key) => {
    let newSorting = {
      key,
      order: 'asc',
    }
    
    if (sorting.key === key && sorting.order === 'asc') {
      newSorting = {...newSorting, order: 'desc'};
    }
    setSorting(newSorting);
  }

  const toggleSelectedAllItems = () => {
    const pageIds = pagedRowsForList.map((event) => getMedicalEventId(event)).filter(Boolean);
    const allPageSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedItems.includes(id));
    if (!allPageSelected) {
      setSelectedItems(pageIds);
    } else {
      setSelectedItems([]);
    }
  }

  const toggleItem = (id) => {
    if (!id) return;
    if (selectedItems.includes(id)) {
      const newSelectedItems = [...selectedItems].filter((item) => item !== id);
      setSelectedItems(newSelectedItems);
    } else {
      const newSelectedItems = [...selectedItems, id];
      setSelectedItems(newSelectedItems);
    }
  }

  const checkSelectAll = () => {
    if (pagedRowsForList.length === 0) return false;
    return pagedRowsForList.every((event) => {
      const eventId = getMedicalEventId(event);
      return eventId && selectedItems.includes(eventId);
    });
  }

  const handlePageClick = (event) => {
    const newOffset = event.selected * MEDICAL_LIST_PAGE_SIZE;
    setItemOffset(newOffset);
    setCurrentPage(event.selected + 1);
  };

  const handlePageSelect = (pageNo) => {
    const parsed = parseInt(pageNo, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return;
    const clamped = Math.min(parsed, pageCount);
    const newOffset = (clamped - 1) * MEDICAL_LIST_PAGE_SIZE;
    setItemOffset(newOffset);
    setCurrentPage(clamped);
  };

  const generateMedicalNotificationPdf = () => {
    if (selectedItems?.length <= 0) {
      window.alert("Please select at least one event record")
    } else {
      const inputData = { eventIds: selectedItems };
      EventsService.generatePdf(inputData);
    }
  }

  const generateVisitRecordSheet = async () => {
    if (selectedItems?.length <= 0) {
      window.alert("Please select at least one event record")
    } else {
      const inputData = { eventIds: selectedItems };
      EventsService.generateSheet(inputData);
    }
    
  };

  const generateVisitRecordPDF = async () => {
    if (selectedItems?.length <= 0) {
      window.alert("Please select at least one event record")
    } else {
      const inputData = { eventIds: selectedItems };
      EventsService.generateSheetPDF(inputData);
    }
    
  };

  const generateMedicalNotificationDocs = () => {
    if (selectedItems?.length <= 0) {
      window.alert("Please select at least one event record");
    } else {
      const inputData = { eventIds: selectedItems };
      EventsService.generateDoc(inputData);
    }
	};

  const visibleColumns = useMemo(() => columns.filter((column) => column.show), [columns]);
  const tableWrapRef = usePageScrollPinnedTableHeader([pagedRowsForList, visibleColumns]);

  const table = (rows) => <div className="list row mb-2">
    <div
      ref={tableWrapRef}
      className="col-md-12 events-multiple-table-wrap sticky-table-scroll sticky-table-scroll--appointment-multiday"
    >
      <table className="personnel-info-table personnel-info-table--freeze-leading personnel-info-table--freeze-leading--with-checkbox">
        <thead>
          <tr>
            <th className="th-checkbox" ><input type="checkbox" checked={checkSelectAll()} onChange={() => toggleSelectedAllItems()}></input></th>
            <th className="th-index">No.</th>
            {
              visibleColumns.map((column, index) => (
                <th
                  className={`sortable-header${column.key === 'customer_name' ? ' th-freeze-customer-name' : ''}`}
                  key={column.key || index}
                >
                  {column.label}{' '}
                  <span className="float-right" onClick={() => sortTableWithField(column.key)}>
                    <img src={`/images/${getSortingImg(column.key)}.png`} alt="" />
                  </span>
                </th>
              ))
            }
            <th></th>
          </tr>
          
        </thead>
        <tbody>
          {
            rows.map((medicalEvent, index) => {
              const eventId = getMedicalEventId(medicalEvent);
              return (
              <tr key={eventId || index}>
              <td className="td-checkbox"><input type="checkbox" checked={eventId ? selectedItems.includes(eventId) : false} onChange={() => toggleItem(eventId)}/></td>
              <td className="td-index">{safeItemOffset + index + 1}</td>
              {visibleColumns.map((column) => (
                column.key === 'customer_name' ? (
                  <td key={column.key} className="td-freeze-customer-name">
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline text-start"
                      onClick={() => handleCustomerNameClick(medicalEvent)}
                    >
                      {medicalEvent?.customer_name}
                    </button>
                  </td>
                ) : (
                  <td key={column.key}>{medicalEvent?.[column.key]}</td>
                )
              ))}
              <td>{medicalEvent?.confirmed ? 'Confirmed' : (AuthService.canEditMedicalEvents() ? <button className="btn btn-primary btn-sm me-2 ms-2" onClick={() => confirmEvent(eventId)}>Confirm</button> : 'Pending')}</td>
            </tr>
              );
            })
          }
        </tbody>
      </table>
    </div>
    {visibleRowsForList.length > MEDICAL_LIST_PAGE_SIZE && (
      <div className="col-md-12 pagination-container">
        <ReactPaginate
          forcePage={Math.max(0, currentPage - 1)}
          className="customers-pagination"
          breakLabel="..."
          nextLabel="Next"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="Prev"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-prev-item"
          previousLinkClassName="page-link"
          nextClassName="page-next-item"
          nextLinkClassName="page-link"
          activeClassName="active"
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
        <div className="select-page-container">
          <input
            type="number"
            className="page-picker"
            max={pageCount}
            min={1}
            value={currentPage}
            onChange={(e) => handlePageSelect(e.target.value)}
          />
          <div className="per-page-label">{` of ${pageCount}`}</div>
        </div>
        <div className="select-page-container">
          <span className="per-page-label">{`${MEDICAL_LIST_PAGE_SIZE} / page`}</span>
        </div>
      </div>
    )}
  </div>;

  const cleanFilterAndClose = () => {
    setCustomerTypeFilter('');
    setProgramTypeFilter('');
    setPaySourceFilter('');
    setLanguageSupportFilter('');
    setTransportationSupportFilter('');
    setEyesOnFilter('');
    setNewPatientFilter('');
    setDoctorOrderFilter('');
    setFastingRequiredFilter('');
    setIdNeededFilter('');
    setNeedMedicationListFilter('');
    setDraftCustomerTypeFilter('');
    setDraftProgramTypeFilter('');
    setDraftPaySourceFilter('');
    setDraftLanguageSupportFilter('');
    setDraftTransportationSupportFilter('');
    setDraftEyesOnFilter('');
    setDraftNewPatientFilter('');
    setDraftDoctorOrderFilter('');
    setDraftFastingRequiredFilter('');
    setDraftIdNeededFilter('');
    setDraftNeedMedicationListFilter('');
    setShowFilterDropdown(false);
  }

  const FilterAndClose = () => {
    setCustomerTypeFilter(draftCustomerTypeFilter);
    setProgramTypeFilter(draftProgramTypeFilter);
    setPaySourceFilter(draftPaySourceFilter);
    setLanguageSupportFilter(draftLanguageSupportFilter);
    setTransportationSupportFilter(draftTransportationSupportFilter);
    setEyesOnFilter(draftEyesOnFilter);
    setNewPatientFilter(draftNewPatientFilter);
    setDoctorOrderFilter(draftDoctorOrderFilter);
    setFastingRequiredFilter(draftFastingRequiredFilter);
    setIdNeededFilter(draftIdNeededFilter);
    setNeedMedicationListFilter(draftNeedMedicationListFilter);
    setShowFilterDropdown(false);
  }

  const exportColumns = visibleColumns;
  const exportData = useMemo(() => {
    return visibleRowsForList.map((event) => {
      const row = {};
      exportColumns.forEach((column) => {
        row[column.key] = event?.[column.key] || '';
      });
      return row;
    });
  }, [visibleRowsForList, exportColumns]);

  const getUniqueOptions = (key) => {
    return [...new Set(events.map((event) => `${event?.[key] || ''}`.trim()).filter(Boolean))];
  };

  const customerTypeOptions = useMemo(() => getUniqueOptions('customer_type'), [events]);
  const programTypeOptions = useMemo(() => getUniqueOptions('program_type'), [events]);
  const paySourceOptions = useMemo(() => getUniqueOptions('pay_source'), [events]);
  const languageSupportOptions = useMemo(() => getUniqueOptions('language_support'), [events]);
  const transportationSupportOptions = useMemo(() => getUniqueOptions('transportation_support'), [events]);
  const eyesOnOptions = useMemo(() => getUniqueOptions('eyes_on'), [events]);
  const newPatientOptions = useMemo(() => getUniqueOptions('new_patient'), [events]);
  const doctorOrderOptions = useMemo(() => getUniqueOptions('doctor_order'), [events]);
  const fastingRequiredOptions = useMemo(() => getUniqueOptions('fasting_required'), [events]);
  const idNeededOptions = useMemo(() => getUniqueOptions('id_needed'), [events]);
  const needMedicationListOptions = useMemo(() => getUniqueOptions('need_medication_list'), [events]);

  const MAX_RANGE_MS = MEDICAL_LIST_MAX_RANGE_DAYS * MEDICAL_LIST_MS_PER_DAY;

  // Editing From: keep To no more than 366 days after From (auto-adjust To when it would exceed).
  const handleFromDateChange = (value) => {
    dateEditDebounceRef.current = true;
    setSelectedItems([]);
    setFromDate(value);
    if (value && toDate && toDate.getTime() - value.getTime() > MAX_RANGE_MS) {
      setToDate(new Date(value.getTime() + MAX_RANGE_MS));
    }
  };

  // Editing To: keep From no more than 366 days before To (auto-adjust From when it would exceed).
  const handleToDateChange = (value) => {
    dateEditDebounceRef.current = true;
    setSelectedItems([]);
    setToDate(value);
    if (value && fromDate && value.getTime() - fromDate.getTime() > MAX_RANGE_MS) {
      setFromDate(new Date(value.getTime() - MAX_RANGE_MS));
    }
  };

  const dateRangeAndEntityFilters = (
    <div className="app-main-content-fields-section with-function flex-end events-multiple-entity-filters--top-row">
      <div className="me-4">
        <div className="field-label">From</div>
        <DatePicker
          popperClassName="medical-list-datepicker-popper"
          selected={fromDate}
          onChange={handleFromDateChange}
        />
      </div>
      <div className="me-4">
        <div className="field-label">To</div>
        <DatePicker
          popperClassName="medical-list-datepicker-popper"
          selected={toDate}
          onChange={handleToDateChange}
        />
      </div>
      <div className="events-multiple-entity-filters__field me-4">
        <div className="field-label">Customer</div>
        <MultiSelectDropdown
          value={selectedCustomerFilters}
          onChange={setSelectedCustomerFilters}
          options={customerFilterOptions}
          placeholder="Select..."
        />
      </div>
      <div className="events-multiple-entity-filters__field">
        <div className="field-label">Resource</div>
        <MultiSelectDropdown
          value={selectedResourceFilters}
          onChange={setSelectedResourceFilters}
          options={resourceFilterOptions}
          placeholder="Select..."
        />
      </div>
    </div>
  );
  
  return (
    <>
      <Modal show={showLoadingOverlay} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Loading Medical Event Multiple Days List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: '8px', fontSize: '13px', color: '#666' }}>
            Fetching appointment data...
          </div>
          <ProgressBar now={loadingProgress} animated label={`${Math.round(loadingProgress)}%`} />
        </Modal.Body>
      </Modal>
      <div className="list row mb-4 noprint">
        <Breadcrumb>
          <Breadcrumb.Item href="/dashboard/dashboard">Medical</Breadcrumb.Item>
          <Breadcrumb.Item active>
            Appointment Information (Multi Days)
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>
             Medical Event Multiple Days List 
             {/* <button className="btn btn-primary btn-sm" onClick={() => {goToCreateNew()}}>Create New Customer</button> 
             <button className="btn btn-link btn-sm" onClick={() => {redirectToAdmin()}}>Back</button> */}
           </h4> 
        </div>
      </div>
      
      <div className="app-main-content-list-container list-page events-multiple-list">
				<div className="app-main-content-list-func-container">
					<div className="tab-pane active">
              {dateRangeAndEntityFilters}
              {table(pagedRowsForList)}
              </div>
          <div className="list-func-panel d-flex flex-wrap align-items-center">
						{/* <input className="me-2 with-search-icon" type="text" placeholder="Search" value={keyword}  onChange={(e) => setKeyword(e.currentTarget.value)} /> */}
						{/* <button className="btn btn-primary me-2"><Filter size={16} className="me-2"></Filter>Filter</button> */}
            <div className="form-check me-3 mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="events-multiple-show-inactive"
                checked={showInactiveAppointmentsOnly}
                onChange={(e) => {
                  setShowInactiveAppointmentsOnly(e.target.checked);
                  setSelectedItems([]);
                }}
              />
              <label className="form-check-label" htmlFor="events-multiple-show-inactive">
                Show inactive appointment
              </label>
            </div>
            <Dropdown
              key={'filter-event-multi-list'}
              id="filter-event-multi-list"
              className="me-2"
              show={showFilterDropdown}
              onToggle={(isOpen, _event, metadata) => {
                // Keep close behavior from outside clicks / ESC.
                if (metadata?.source === 'rootClose' || metadata?.source === 'keydown') {
                  setShowFilterDropdown(isOpen);
                }
              }}
              autoClose="outside"
            > 
              <Dropdown.Toggle
                variant="outline-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  const nextOpen = !showFilterDropdown;
                  if (nextOpen) {
                    // Open with current applied values so changes are staged until "Filter" is tapped.
                    setDraftCustomerTypeFilter(customerTypeFilter);
                    setDraftProgramTypeFilter(programTypeFilter);
                    setDraftPaySourceFilter(paySourceFilter);
                    setDraftLanguageSupportFilter(languageSupportFilter);
                    setDraftTransportationSupportFilter(transportationSupportFilter);
                    setDraftEyesOnFilter(eyesOnFilter);
                    setDraftNewPatientFilter(newPatientFilter);
                    setDraftDoctorOrderFilter(doctorOrderFilter);
                    setDraftFastingRequiredFilter(fastingRequiredFilter);
                    setDraftIdNeededFilter(idNeededFilter);
                    setDraftNeedMedicationListFilter(needMedicationListFilter);
                  }
                  setShowFilterDropdown(nextOpen);
                }}
              >
                <Filter size={16} className="me-2"></Filter>Filter
              </Dropdown.Toggle>
              <Dropdown.Menu
                as={EventsMultipleFilterMenu}
                align="start"
                popperConfig={filterMenuPopperConfig}
                draftCustomerTypeFilter={draftCustomerTypeFilter}
                setDraftCustomerTypeFilter={setDraftCustomerTypeFilter}
                customerTypeOptions={customerTypeOptions}
                draftProgramTypeFilter={draftProgramTypeFilter}
                setDraftProgramTypeFilter={setDraftProgramTypeFilter}
                programTypeOptions={programTypeOptions}
                draftPaySourceFilter={draftPaySourceFilter}
                setDraftPaySourceFilter={setDraftPaySourceFilter}
                paySourceOptions={paySourceOptions}
                draftLanguageSupportFilter={draftLanguageSupportFilter}
                setDraftLanguageSupportFilter={setDraftLanguageSupportFilter}
                languageSupportOptions={languageSupportOptions}
                draftTransportationSupportFilter={draftTransportationSupportFilter}
                setDraftTransportationSupportFilter={setDraftTransportationSupportFilter}
                transportationSupportOptions={transportationSupportOptions}
                draftEyesOnFilter={draftEyesOnFilter}
                setDraftEyesOnFilter={setDraftEyesOnFilter}
                eyesOnOptions={eyesOnOptions}
                draftNewPatientFilter={draftNewPatientFilter}
                setDraftNewPatientFilter={setDraftNewPatientFilter}
                newPatientOptions={newPatientOptions}
                draftDoctorOrderFilter={draftDoctorOrderFilter}
                setDraftDoctorOrderFilter={setDraftDoctorOrderFilter}
                doctorOrderOptions={doctorOrderOptions}
                draftFastingRequiredFilter={draftFastingRequiredFilter}
                setDraftFastingRequiredFilter={setDraftFastingRequiredFilter}
                fastingRequiredOptions={fastingRequiredOptions}
                draftIdNeededFilter={draftIdNeededFilter}
                setDraftIdNeededFilter={setDraftIdNeededFilter}
                idNeededOptions={idNeededOptions}
                draftNeedMedicationListFilter={draftNeedMedicationListFilter}
                setDraftNeedMedicationListFilter={setDraftNeedMedicationListFilter}
                needMedicationListOptions={needMedicationListOptions}
                cleanFilterAndClose={cleanFilterAndClose}
                FilterAndClose={FilterAndClose}
              />
            </Dropdown>
						<ManageTable columns={columns} onColumnsChange={setColumns} />
						{AuthService.canEditMedicalEvents() && <button className="btn btn-primary me-2" onClick={() => goToCreateNew()}><Plus size={16}></Plus>Add New Medical Appointment</button>}
						<Export 
							columns={exportColumns} 
							data={exportData} 
							filename="events-multiple"
							actionLogPageName="Medical Event Multiple Days List"
							customActions={[
								{ label: 'Medical Notifications Doc', onClick: generateMedicalNotificationDocs },
								{ label: 'Medical Notifications Pdf', onClick: generateMedicalNotificationPdf },
								{ label: 'Visit Record Sheet', onClick: generateVisitRecordSheet },
								{ label: 'Visit Record PDF', onClick: generateVisitRecordPDF }
							]}
						/>
					</div>
        </div>
      </div>
    </>
  )
};

export default EventsMultipleList;